// ============================================================
// ClientPlatformConfigTab.js
// Sixth tab on the Client Detail page. Eleven collapsible sub-cards,
// each tied to a per-client config endpoint.
//
//   1. Event Types       — IsEnabled toggle + TitleOverride per type
//   2. Training Types    — IsEnabled + custom expiry months per type
//   3. Survey Cycle      — start month + interval + low-response threshold
//   4. Branding          — logo upload + primary colour + preview
//   5. Notification Channels — per-type Teams/Email toggles
//   6. Audit Schedules   — read-only list w/ Add Schedule
//
// Each card is self-contained (own fetch + save + state) so a
// failure in one doesn't take the others down.
// ============================================================

import React, { useState } from 'react';
import EventTypesCard           from './platform-config/EventTypesCard';
import TrainingTypesCard        from './platform-config/TrainingTypesCard';
import SurveyCycleCard          from './platform-config/SurveyCycleCard';
import BrandingCard             from './platform-config/BrandingCard';
import NotificationChannelsCard from './platform-config/NotificationChannelsCard';
import AuditSchedulesCard       from './platform-config/AuditSchedulesCard';
import HrModelCard              from './platform-config/HrModelCard';
import CarePeopleReconcileCard from './platform-config/CarePeopleReconcileCard';
import GeofenceConfigCard       from './platform-config/GeofenceConfigCard';
import M365LifecycleCard        from './platform-config/M365LifecycleCard';
import SharedCalendarCard      from './platform-config/SharedCalendarCard';
import EmergencyPackCard       from './platform-config/EmergencyPackCard';
import SampleDataCard          from './platform-config/SampleDataCard';
import LinkedClientsCard       from './platform-config/LinkedClientsCard';
import './platform-config/PlatformConfigTab.css';

const CARDS = [
  { id: 'event-types',           title: 'Event Types',           Component: EventTypesCard },
  { id: 'training-types',        title: 'Training Types',        Component: TrainingTypesCard },
  { id: 'survey-cycle',          title: 'Survey Cycle',          Component: SurveyCycleCard },
  { id: 'branding',              title: 'Branding',              Component: BrandingCard },
  { id: 'notification-channels', title: 'Notification Channels', Component: NotificationChannelsCard },
  { id: 'audit-schedules',       title: 'Audit Schedules',       Component: AuditSchedulesCard },
  { id: 'hr-model',              title: 'CarePeople HR Model',    Component: HrModelCard },
  { id: 'carepeople-reconcile',  title: 'CarePeople Staff Profiles', Component: CarePeopleReconcileCard },
  { id: 'geofence-config',       title: 'CareRota Geofence',      Component: GeofenceConfigCard },
  { id: 'm365-lifecycle',        title: 'M365 Lifecycle',         Component: M365LifecycleCard },
  { id: 'shared-calendar',       title: 'Shared Leave Calendar', Component: SharedCalendarCard },
  { id: 'emergency-pack',        title: 'Emergency Pack Site',    Component: EmergencyPackCard },
  { id: 'sample-data',           title: 'Sample Data',            Component: SampleDataCard },
  { id: 'linked-clients',        title: 'Linked Clients',         Component: LinkedClientsCard },
];

export default function ClientPlatformConfigTab({ clientId, clientName }) {
  const [expanded, setExpanded] = useState(() => new Set(['event-types']));

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="platform-config">
      {CARDS.map(({ id, title, Component }) => {
        const isOpen = expanded.has(id);
        return (
          <section key={id} className="platform-config__card">
            <button
              type="button"
              className="platform-config__card-head"
              aria-expanded={isOpen}
              onClick={() => toggle(id)}
            >
              <span className="platform-config__card-title">{title}</span>
              <span className="platform-config__card-chevron">{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
              <div className="platform-config__card-body">
                <Component clientId={clientId} clientName={clientName} />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
